'use client';

import { useEffect, useState } from 'react';
import { Icon } from '../icon';
import { Logo, useReveal } from '../primitives';
import { href, nav as defaultNav } from '../data';
import type { NavLink } from '../data';

/** Fixed top nav with scroll-glass + mobile menu. Ported from `site/nav.jsx`. */
export function Nav({ links, cta, current }: { links?: NavLink[]; cta?: string; current?: string }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 12);
    on();
    window.addEventListener('scroll', on, { passive: true });
    return () => window.removeEventListener('scroll', on);
  }, []);
  const navLinks = links || defaultNav;
  const ctaHref = cta || href('enroll');
  return (
    <header className={`nav ${scrolled ? 'scrolled' : ''}`}>
      <div className="container nav-inner">
        <a href={href('home')} aria-label="FRAUDefender home">
          <Logo />
        </a>
        <nav className="nav-links collapsible">
          {navLinks.map((l) => (
            <a key={l.href} href={l.href} className={`nav-link ${current === l.label ? 'active' : ''}`}>
              {l.label}
            </a>
          ))}
        </nav>
        <div className="row" style={{ gap: 10 }}>
          <a href={href('login')} className="btn btn-quiet btn-sm" style={{ display: 'inline-flex' }}>
            <Icon name="lock" size={15} />
            Log in
          </a>
          <a href={ctaHref} className="btn btn-primary btn-sm">
            Book a demo
            <Icon name="arrow-right" size={16} />
          </a>
          <button
            className="btn btn-ghost btn-sm nav-burger"
            aria-label="Menu"
            onClick={() => setOpen((o) => !o)}
            style={{ width: 38, padding: 0, display: 'none' }}
          >
            <Icon name={open ? 'x' : 'menu'} size={18} />
          </button>
        </div>
      </div>
      {open && (
        <div className="container" style={{ paddingBottom: 16 }}>
          <div className="card card-pad stack" style={{ gap: 4 }}>
            {navLinks.map((l) => (
              <a key={l.href} href={l.href} className="nav-link" onClick={() => setOpen(false)} style={{ padding: '12px 10px' }}>
                {l.label}
              </a>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}

/** Closing call-to-action band. Ported from `site/nav.jsx`. */
export function CTABand() {
  useReveal();
  return (
    <section className="section" id="cta">
      <div className="container">
        <div className="card reveal" style={{ position: 'relative', overflow: 'hidden', borderColor: 'var(--line)', padding: 'clamp(34px, 6vw, 72px)' }}>
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'radial-gradient(700px 320px at 80% 0%, oklch(0.5 0.17 258 / 0.35), transparent 62%)',
              pointerEvents: 'none',
            }}
          />
          <div className="grid-bg" style={{ opacity: 0.3 }} />
          <div
            style={{ position: 'relative', display: 'grid', gap: 36, gridTemplateColumns: 'minmax(0,1fr) auto', alignItems: 'center' }}
            className="cta-grid"
          >
            <div className="stack" style={{ gap: 18, maxWidth: 620 }}>
              <span className="eyebrow">Identify · Protect · Defend</span>
              <h2 className="h2 balance" style={{ margin: 0 }}>
                Put the full dispute lifecycle behind every transaction.
              </h2>
              <p className="lead pretty" style={{ margin: 0 }}>
                See the agentic engine resolve live cases, model your VAMP exposure, and enroll the services you need — one
                integration, worldwide issuer reach.
              </p>
            </div>
            <div className="stack" style={{ gap: 12, minWidth: 230 }}>
              <a href={href('enroll')} className="btn btn-primary btn-lg" style={{ width: '100%' }}>
                Start enrollment
                <Icon name="arrow-right" size={18} />
              </a>
              <a href={href('demo')} className="btn btn-ghost btn-lg" style={{ width: '100%' }}>
                Book a demo
              </a>
              <div className="row subtle" style={{ gap: 8, fontSize: 13, justifyContent: 'center' }}>
                <Icon name="lock" size={14} /> Pick only the services you want
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/** Site footer. Ported from `site/nav.jsx`. */
export function Footer() {
  const H = href;
  const cols: { h: string; items: [string, string][] }[] = [
    { h: 'Lifecycle', items: [['Prevent', H('lifecycle')], ['Resolve', H('lifecycle')], ['Inform', H('lifecycle')], ['Recover', H('engine')]] },
    {
      h: 'Products',
      items: [
        ['Order Insight', H('products')],
        ['PREVENT+ / CE3', H('products')],
        ['RDR', H('products')],
        ['CDRN', H('products')],
        ['MC Resolve', H('products')],
        ['INFORM', H('products')],
      ],
    },
    { h: 'Company', items: [['About', 'https://about.fraudefender.com'], ['VAMP guidance', H('vamp')], ['Enroll', H('enroll')], ['Log in', H('login')]] },
  ];
  return (
    <footer className="footer">
      <div className="container section-tight">
        <div style={{ display: 'grid', gap: 40, gridTemplateColumns: '1.4fr repeat(3, 1fr)' }} className="footer-grid">
          <div className="stack" style={{ gap: 16, maxWidth: 320 }}>
            <Logo />
            <p className="muted pretty" style={{ margin: 0, fontSize: 14 }}>
              Advanced fraud prevention and dispute resolution for processors, PSPs and merchants. A product of ICS Access LLC.
            </p>
            <div className="row" style={{ gap: 8 }}>
              <span className="tag tag-blue">Identify</span>
              <span className="tag tag-cyan">Protect</span>
              <span className="tag tag-green">Defend</span>
            </div>
          </div>
          {cols.map((c) => (
            <div key={c.h} className="stack" style={{ gap: 12 }}>
              <div className="mono subtle" style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                {c.h}
              </div>
              {c.items.map(([t, h]) => (
                <a key={t} href={h} className="muted" style={{ fontSize: 14 }}>
                  {t}
                </a>
              ))}
            </div>
          ))}
        </div>
        <div className="rule" style={{ margin: '36px 0 22px' }} />
        <div className="between wrap" style={{ gap: 12, fontSize: 13 }}>
          <span className="subtle">© 2026 ICS Access LLC · PO Box 188, Red Hook, NY 12571</span>
          <span className="subtle mono">about.fraudefender.com</span>
        </div>
      </div>
    </footer>
  );
}
